import React, { useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { Space, message, notification, Image, Row, Col, Radio } from 'antd'

import {
  PlusOutlined,
  PrinterOutlined,
  CloseOutlined,
  QrcodeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  TagsOutlined,
  IdcardOutlined
} from '@ant-design/icons'

// Import custom components
import Button from '../../components/common/button/button'
import Input from '../../components/common/input/input'
import Modal from '../../components/common/modal/modal'
import Table from '../../components/common/table/table'
import Spinner from '../../components/common/spinner/spinner'
import Typography from '../../components/common/typography/typography'
import VideoCapture from '../../components/barcodeScanner/BarcodeScanner'
import ClearanceLabelsPrintPreview from '../../components/clearanceLabels/ClearanceLabelsPrintPreview'

// Import assets
import noDataImage from '../../assets/images/noDataImage.png'
import savedImage from '../../assets/images/savedItem.png'

// Import utility functions
import {
  handleQuantityChange,
  handleSaveAPN,
  handleBarcodeScan,
  exportMissingAPNs,
  detectCognitoUser
} from '../clearanceLabels/addClearanceLabelsUtils'
import { fetchClearanceLabel } from '../../api/clearanceLabelApiService/clearanceLabelApiUtils'

// Import styles
import './addClearanceLabels.scss'

const { Title, Text } = Typography

const ClearanceLabels = () => {
  // State declarations
  const [isReloading, setIsReloading] = useState(false)
  const [rows, setRows] = useState([])
  const [open, setOpen] = useState(false)
  const [apnInput, setApnInput] = useState('')
  const [error, setError] = useState('')
  const [selectedRows, setSelectedRows] = useState([])
  const [scannedApns, setScannedApns] = useState(new Set())
  const [missingAPNs] = useState([])
  const [missingAPNDialogOpen, setMissingAPNDialogOpen] = useState(false)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [isScannerActive, setIsScannerActive] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700)
  const [showPrintPreview, setShowPrintPreview] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [labelLayout, setLabelLayout] = useState('detailed')
  const [, setIsCognitoUser] = useState(false)

  const printPreviewRef = useRef()
  const { instance } = useMsal()

  // Create a style element for print settings
  const pageStyle = `
    @media print {
      @page {
        size: A4;
        margin: 0;
      }
      body {
        -webkit-print-color-adjust: exact;
      }
      
      /* Hide everything except print content */
      body * {
        visibility: hidden;
      }
      
      .print-only-content,
      .print-only-content * {
        visibility: visible !important;
      }
      
      /* Position the print content */
      .print-only-content {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }
    }
  `

  // Detect authentication type
  useEffect(() => {
    const checkAuthType = async () => {
      try {
        const cognitoUser = await detectCognitoUser(instance)
        setIsCognitoUser(cognitoUser)
      } catch (error) {
        console.error('Error detecting auth type:', error)
        setIsCognitoUser(false)
      }
    }

    checkAuthType()
  }, [instance])

  // Check for mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 700)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Add and remove print style when printer type changes
  useEffect(() => {
    // Create or update the style element
    let styleElement = document.getElementById('print-style-element')

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = 'print-style-element'
      document.head.appendChild(styleElement)
    }

    styleElement.textContent = pageStyle

    return () => {
      if (styleElement) {
        document.head.removeChild(styleElement)
      }
    }
  }, [pageStyle])

  // Table columns configuration
  const columns = [
    {
      title: 'Keycode',
      dataIndex: 'apn',
      key: 'apn'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (text, record) => (
        <Input
          type="number"
          min={1}
          value={text}
          onChange={(e) =>
            handleQuantityChange(record.id, parseInt(e.target.value), setRows)
          }
          className="quantity-input"
        />
      )
    }
  ]

  // Handle print - fetch real label data then show print preview
  const onGenerateLabels = async () => {
    if (selectedRows.length === 0) {
      message.warning('Please select items to print')
      return
    }

    const selectedRowsData = rows.filter(row => selectedRows.includes(row.id))
    const keyCodes = selectedRowsData.map(row => String(row.apn))

    try {
      setIsReloading(true)
      const response = await fetchClearanceLabel(instance, keyCodes)
      const labelData = response?.data || []

      // Build a map of keyCode -> price data from API response
      const priceMap = {}
      labelData.forEach(item => {
        priceMap[item.keyCode] = item
      })

      // Keycodes not returned by the API at all (invalid)
      const invalidKeycodes = selectedRowsData.filter(row => !priceMap[String(row.apn)])

      // Keycodes returned by API but without originalPrice (not on clearance)
      const notOnClearance = selectedRowsData.filter(row => priceMap[String(row.apn)] && !priceMap[String(row.apn)]?.originalPrice)

      if (invalidKeycodes.length > 0) {
        notification.error({
          message: 'Invalid Keycode(s)',
          description: `The following keycode(s) are invalid: ${invalidKeycodes.map(r => r.apn).join(', ')}`,
          duration: 0,
          placement: 'top'
        })
      }

      if (notOnClearance.length > 0) {
        notification.warning({
          message: 'Not on Clearance',
          description: `The following keycode(s) are not on clearance: ${notOnClearance.map(r => r.apn).join(', ')}`,
          duration: 0,
          placement: 'top'
        })
      }

      const validRows = selectedRowsData.filter(row => !!priceMap[String(row.apn)]?.originalPrice)

      if (validRows.length === 0) {
        return
      }

      // Expand items based on quantity, using API prices
      const expandedPrintList = []
      validRows.forEach(row => {
        const priceInfo = priceMap[String(row.apn)] || {}
        for (let i = 0; i < row.quantity; i++) {
          expandedPrintList.push({
            apn: row.apn,
            productDescription: priceInfo.productDescription || '',
            wasPrice: priceInfo.originalPrice,
            currentPrice: priceInfo.recommendRetailPriceIncTaxInPesos ?? 0
          })
        }
      })

      setPrintData({ printList: expandedPrintList })
      setShowPrintPreview(true)

      setTimeout(() => {
        const styleElement = document.createElement('style')
        styleElement.id = 'print-fixes-style'

        styleElement.textContent = `
          @media print {
            @page {
              size: A4;
              margin: 0;
            }
            
            html, body {
              height: auto !important;
              overflow: visible !important;
            }
          }
        `

        document.head.appendChild(styleElement)
        window.print()

        setTimeout(() => {
          if (document.getElementById('print-fixes-style')) {
            document.head.removeChild(document.getElementById('print-fixes-style'))
          }
          setShowPrintPreview(false)
        }, 500)
      }, 300)
    } catch (error) {
      const errorMsg = error?.response?.data?.error || 'Failed to fetch label data'
      message.error(`${errorMsg}: ${keyCodes.join(', ')}`)
    } finally {
      setIsReloading(false)
    }
  }

  const onSaveAPN = () => {
    handleSaveAPN(apnInput, setError, setRows, setOpen, setApnInput)
  }

  const onDeleteSelected = () => {
    if (selectedRows.length === 0) {
      message.warning('Please select items to delete')
      return
    }
    setRows((prevRows) => prevRows.filter((row) => !selectedRows.includes(row.id)))
    setSelectedRows([])
    message.success('Selected items deleted')
  }

  const onBarcodeScan = (barcodeResult) => {
    handleBarcodeScan(
      barcodeResult,
      scannedApns,
      setRows,
      setScannedApns,
      setIsScannerActive
    )
  }

  const onExportMissingAPNs = () => {
    exportMissingAPNs(missingAPNs)
  }

  return (
    <main >
      {!isScannerActive && (
        <>
          <Title level={4} className="clearance-labels-title">
            Add Clearance Labels
          </Title>
          <hr></hr>
        </>
      )}
      <Row
        justify="space-between"
        align="middle"
        className="clearance-labels-action-row"
      >
        {!isScannerActive && (
          <>
            <Col>
              <Space>
                {rows.length > 0 && (
                  <>
                    <Button
                      icon={<PrinterOutlined />}
                      disabled={selectedRows.length === 0}
                      onClick={onGenerateLabels}
                    >
                      {!isMobile && 'Print'}
                    </Button>
                    <Button
                      icon={<DeleteOutlined />}
                      type="default"
                      disabled={selectedRows.length === 0}
                      onClick={onDeleteSelected}
                      style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
                    >
                      {!isMobile && 'Delete'}
                    </Button>
                  </>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setOpen(true)}
                >
                  {!isMobile && 'Add Keycode'}
                </Button>
                {isMobile && (
                  <Button
                    type="primary"
                    icon={<QrcodeOutlined />}
                    onClick={() => setIsScannerActive(true)}
                  >
                    {!isMobile && 'Scan Barcode'}
                  </Button>
                )}
              </Space>
            </Col>
          </>
        )}
      </Row>

      {rows.length > 0 && !isScannerActive && (
        <div style={{ marginBottom: 16, padding: '10px 0' }}>
          <span style={{ fontSize: 17, color: '#333', marginRight: 16, fontWeight: 600 }}>Layout: </span>
          <Radio.Group className="layout-radio-group" value={labelLayout} onChange={e => setLabelLayout(e.target.value)}>
            <Radio value="detailed" style={{ fontSize: 16 }}><TagsOutlined style={{ marginRight: 6 }} />Label</Radio>
            <Radio value="classic" style={{ fontSize: 16 }}><IdcardOutlined style={{ marginRight: 6 }} />Shelftalker</Radio>
          </Radio.Group>
        </div>
      )}

      {isScannerActive ? (
        <div className="scanner-section">
          <div className="scanner-header">
            <Button
              type="primary"
              danger
              icon={<CloseOutlined />}
              onClick={() => setIsScannerActive(false)}
              className="scanner-cancel-btn"
            >
              Cancel
            </Button>
          </div>
          <div className="scanner-container-embedded">
            <VideoCapture onBarcodeScanned={onBarcodeScan} />
          </div>
        </div>
      ) : (
        rows.length > 0 ? (
          <div style={{ position: 'relative', minHeight: '400px' }}>
            {isReloading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Spinner style={{ transform: 'scale(1.5)' }} />
              </div>
            )}
            <Table
              dataSource={rows}
              columns={columns}
              rowKey="id"
              rowSelection={{
                selectedRowKeys: selectedRows,
                onChange: setSelectedRows
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: false
              }}
              className="clearance-labels-table"
              style={{ opacity: isReloading ? 0.5 : 1 }}
            />
          </div>
        ) : (
          <div className="clearance-labels-no-data">
            <Image
              src={noDataImage}
              alt="No Data"
              preview={false}
              className="clearance-labels-no-data-image"
            />
          </div>
        )
      )}

      {/* Add Keycode Modal */}
      <Modal
        title="Add Keycode"
        open={open}
        onOk={onSaveAPN}
        onCancel={() => {
          setOpen(false)
          setError('')
          setApnInput('')
        }}
        okText="Add"
        cancelText="Close"
        className="clearance-labels-modal"
      >
        <Input
          type="textarea"
          placeholder="Enter keycode(s)"
          value={apnInput}
          onChange={(e) => {
            setApnInput(e.target.value)
          }}
          rows={4}
          status={error ? 'error' : ''}
          className="clearance-labels-apn-input"
        />
        {error && (
          <Text type="danger" className="error-text">
            {error}
          </Text>
        )}
      </Modal>

      {/* Print Preview Content - A4 Label Format - Only shown during print */}
      {showPrintPreview && printData && (
        <div ref={printPreviewRef}>
          <ClearanceLabelsPrintPreview printList={printData.printList} layout={labelLayout} />
        </div>
      )}

      {/* Remaining Modals */}

      {/* Missing Keycodes Modal */}
      <Modal
        title={
          <div className="clearance-labels-missing-header">
            <span>Invalid Keycodes</span>
            <Space>
              <Button icon={<DownloadOutlined />} onClick={onExportMissingAPNs}>
                Download
              </Button>
            </Space>
          </div>
        }
        open={missingAPNDialogOpen}
        onCancel={() => setMissingAPNDialogOpen(false)}
        footer={null}
        className="clearance-labels-modal"
      >
        <Table
          dataSource={missingAPNs.map((apn, index) => ({
            key: index,
            id: index + 1,
            apn: apn
          }))}
          columns={[
            { title: 'No.', dataIndex: 'id', width: 100 },
            { title: 'Invalid Keycode', dataIndex: 'apn', flex: 1 }
          ]}
          pagination={false}
          scroll={{ y: 300 }}
          className="invalid-apn-table"
          rowKey="key"
        />
      </Modal>

      {/* Success Modal */}
      <Modal
        open={imageDialogOpen}
        onCancel={() => {
          setImageDialogOpen(false)
          setRows([])
        }}
        footer={[
          <Button
            key="back"
            type="primary"
            onClick={() => {
              setImageDialogOpen(false)
              setRows([])
            }}
          >
            OK
          </Button>
        ]}
        className="clearance-labels-success-modal"
      >
        <div className="clearance-labels-success-content">
          <Image src={savedImage} alt="Saved Successfully" preview={false} />
          <Title level={2}>Thank You</Title>
          <Text>Clearance label report saved successfully.</Text>
        </div>
      </Modal>
    </main>
  )
}

export default ClearanceLabels
